<template>
  <el-container>
    <el-main>
      <h3 class="heading">
        New creative
        <span v-if="campaign"> in campaign {{ campaign.name }} </span>
      </h3>
      <el-card>
        <CreativeForm
          v-if="campaign && creativeItem && !loading"
          v-model="creativeItem"
          :loading="loading"
          action-text="Create"
          traffic-filters-default-values
          @submit="onSubmit"
        />
      </el-card>
      <LoadingBlock :loading="fetchingCampaign || loading" />
    </el-main>
  </el-container>
</template>

<script lang="ts">
import { defineComponent, ref, watch, computed } from 'vue'
import CreativeForm from '@/apps/traforama/views/creatives/components/CreativeForm.vue'
import LoadingBlock from '@/components/LoadingBlock.vue'
import useFetchCampaign from '@/hooks/useFetchCampaign'
import {
  CreativeValidationErrorsKey,
  VideoSource,
  ProxyType
} from '@/api/types'
import { ICreativeAttributes, ICreative } from '@/api/creatives'
import useValidation from '@/hooks/useValidation'
import useRouter from '@/hooks/useRouter'
import { ElMessage } from 'element-plus'
import {
  useCreativesStore,
  defaultAutoCpaSetting
} from '@/apps/traforama/store/creatives'
import useFetchCreative from '@/hooks/useFetchCreative'
import { useUserStore } from '@/store/modules/user'
import { safeSubmitProcessing } from '@/utils/helpers'
import { NO_FUNDS_STATE } from '@/apps/traforama/constants'
import { useOnboardingRedirect } from '@/hooks/useOnboardingRedirect'
import { AxiosError, isAxiosError } from 'axios'

const CREATIVE_ASSETS_KEYS = ['images', 'icons', 'videos']

export default defineComponent({
  name: 'NewCreative',
  components: {
    CreativeForm,
    LoadingBlock
  },
  props: {
    campaignId: {
      type: String,
      required: true
    },
    id: {
      type: String,
      default: null
    }
  },
  setup(props) {
    const creativesStore = useCreativesStore()
    const userStore = useUserStore()
    const { campaign, fetchingCampaign, fetchCampaign, provideCampaign } =
      useFetchCampaign()
    fetchCampaign(props.campaignId)
    provideCampaign()

    const { provideValidation, setValidationErrors } = useValidation(
      CreativeValidationErrorsKey
    )
    const errors = provideValidation()
    const loading = ref(false)

    let creativeItem = ref<ICreativeAttributes | undefined>({
      id: undefined,
      name: '',
      advert_state: 'new',
      link_url: '',
      code: '',
      refresh_rate: 0,
      hide_referrer: false,
      smartlink_status: 'direct',
      no_bounces: false,
      skip_if_no_ref: false,
      skip_if_no_cookies: false,
      skip_if_x_requested_with: false,
      no_onloop: false,
      block_proxy_type_ids: [
        ProxyType.Vpn,
        ProxyType.Tor,
        ProxyType.DCH,
        ProxyType.PublicProxy,
        ProxyType.WebProxy,
        ProxyType.SearchEngine
      ],
      only_proxy_traffic: false,
      prices: [],
      image: undefined,
      video: undefined,
      icon: undefined,
      skip_offset: 5,
      asset_processed: false,
      show_processing: false,
      force_review: false,
      campaign_id: parseInt(props.campaignId),
      cpm_calculation_type: userStore.allowSmartCpm ? 'smart' : 'fixed',
      video_source: VideoSource.Url,
      available_cpm_types: userStore.availableCpmTypes,
      auto_cpa_setting_attributes: defaultAutoCpaSetting
    })

    const creativesToClone = computed(() => {
      return creativesStore.getClonedItems
    })

    const fillCreativeForm = (id: any) => {
      loading.value = true
      creativesStore.FetchItem(id).then(() => {
        const storedItem = creativesStore.itemById(props.id)
        // @ts-ignore: Type 'ICreativeAttributes' is not assignable to type 'undefined.
        creativeItem.value = { ...storedItem }
        creativeItem.value.id = undefined
        creativeItem.value.campaign_id = parseInt(props.campaignId)
        creativeItem.value.image_preview = undefined
        creativeItem.value.video_preview = undefined
        creativeItem.value.force_review = false
        loading.value = false
      })
    }

    watch(
      (_: any) => props.id,
      (val: any) => {
        fillCreativeForm(val)
      }
    )

    if (props.id) {
      fillCreativeForm(props.id)
    }

    const { router } = useRouter()
    const { processOnboarding } = useOnboardingRedirect()

    const onSuccess = async (item: ICreative) => {
      if (await processOnboarding()) {
        return
      }

      if (creativesToClone.value.length) {
        creativesStore.RemoveClonedItem(parseInt(creativesToClone.value[0]))
      }
      const route = creativesToClone.value.length
        ? `/campaigns/${props.campaignId}/creatives/${creativesToClone.value[0]}/clone`
        : '/campaigns'
      router.push(route)

      if (item.attributes.advert_state === NO_FUNDS_STATE) {
        ElMessage.error({
          message: 'YOUR CREATIVE WILL BE REVIEWED AFTER BALANCE REPLENISHMENT',
          showClose: true,
          duration: 15000
        })
      } else {
        ElMessage.success({ message: 'Creative has been created' })
      }
    }

    const errorMessage = (err: unknown) =>
      (isAxiosError(err) && err.response?.data?.message) || 'Upload failed'

    const createOneByOne = async (
      creative: ICreativeAttributes,
      assetKey: string
    ) => {
      let lastCreated: ICreative | undefined
      let firstError: unknown
      const failed: string[] = []

      loading.value = true
      for (const [index, file] of creative[assetKey].entries()) {
        try {
          lastCreated = await creativesStore.SaveItem({
            ...creative,
            // Same names as the backend's batch create gives: `name`, `name #2`, …
            name: index ? `${creative.name} #${index + 1}` : creative.name,
            [assetKey]: [file]
          })
        } catch (err) {
          firstError ??= err
          failed.push(`${file.name}: ${errorMessage(err)}`)
        }
      }
      loading.value = false

      if (!lastCreated) {
        setValidationErrors(firstError as AxiosError, errors)
        return
      }
      if (failed.length) {
        ElMessage.error({
          message: `Not created: ${failed.join('; ')}`,
          showClose: true,
          duration: 0
        })
      }
      await onSuccess(lastCreated)
    }

    const onSubmit = async ({
      creative
    }: {
      creative: ICreativeAttributes
    }) => {
      errors.value = {}

      const assetKey = CREATIVE_ASSETS_KEYS.find(
        (key) => creative[key]?.length > 1
      )
      if (assetKey) {
        await createOneByOne(creative, assetKey)
        return
      }

      await safeSubmitProcessing(
        loading,
        () => creativesStore.SaveItem(creative),
        (item: ICreative) => onSuccess(item),
        (err) => setValidationErrors(err, errors)
      )
    }

    return { campaign, fetchingCampaign, creativeItem, loading, onSubmit }
  }
})
</script>
